import { useState } from "react";
import { BackButton } from "../../../../components/buttons/backButton";
import { NumberCard } from "../../../../components/cards/numberCard";
import { EventCardSkeleton } from "../../../../components/event/eventCard";
import { Pill } from "../../../../components/pill";
import { TableComponent } from "../../../../components/table";
import { BgWrapper } from "../../../../components/wrapper/bgWrapper";
import { UserSalesCard } from "./userSalesCard";
import {
  useGetAgentPerformanceQuery,
  useGetSalesGuestQuery,
  useGetSingleEventBySlugQuery,
} from "../../../../redux/features/sales/sales.api";
import { Guest, QueryParams } from "../../../../utils/types";
import { useParams } from "react-router-dom";
import { ProfileIcon } from "../../../../components/cards/profilePic";
import { formatDateAndTime } from "../../../../utils/helpers";
import { getColorForTicket } from "../../../../utils/constants";

export const UserSalesPage = () => {
  const { id, slug } = useParams<{ id: string; slug: string }>();

  const [query, setQuery] = useState<QueryParams>({
    page: 1,
    limit: 50,
    search: undefined,
    sortOrder: "desc",
  });
  const { isFetching: fetchingEventDetails, data: guestData } =
    useGetSalesGuestQuery(
      {
        id,
        ...query,
        search: query.search === "" ? undefined : query.search,
      },
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const { isFetching: fetchingAgentPerformance, data: agentPerformance } =
    useGetAgentPerformanceQuery(
      {
        id,
      },
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const { isFetching: fetchingBySlug, data: eventData } =
    useGetSingleEventBySlugQuery(
      { slug: slug ?? "" },
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const guests = guestData?.data?.data ?? [];

const totals = guests.reduce(
  (acc, guest) => {
    guest.tickets.forEach((ticket) => {
      acc.sold += ticket.quantity || 0;

      // Only add to available if we haven't seen this ticket type before
      const ticketId = ticket.ticketId?._id;
      if (ticketId && !acc.seenTicketIds.has(ticketId)) {
        acc.available += ticket.ticketId?.quantity || 0;
        acc.seenTicketIds.add(ticketId);
      }
    });
    return acc;
  },
  { sold: 0, available: 0, seenTicketIds: new Set() },
);

  return fetchingBySlug || fetchingEventDetails ? (
    <EventCardSkeleton imageDimension={300} showExtraDetails className="" />
  ) : (
    <div className="mt-3 flex w-full animate-fade-in flex-col gap-1.5">
      <BackButton />
      <UserSalesCard event={eventData?.data} />
      <div className="flex flex-col gap-0.5">
        <BgWrapper className="flex flex-col items-center gap-2.5 !rounded-b-none md:flex-row">
          <NumberCard
            value={`${totals.sold} of ${totals?.available}`}
            text="Tickets Sold"
            isCurrency={false}
            loading={fetchingEventDetails}
            titleFirst
          />
          <NumberCard
            value={agentPerformance?.data?.totalCommission ?? 0}
            text="My Earnings"
            isCurrency={true}
            loading={fetchingAgentPerformance}
            titleFirst
          />
        </BgWrapper>
        <BgWrapper className="flex w-full flex-col gap-2.5 !rounded-t-none">
          <p className="text-base font-bold text-primary">Sales Breakdown</p>
          <TableComponent
            headers={[]}
            rows={
              guestData?.data?.data?.map((user: Guest, idx: number) => {
                const usedColors: string[] = [];

                return {
                  id: idx,
                  items: [
                    <div
                      className={`flex w-full flex-1 flex-col justify-between gap-2 md:flex-row md:items-center`}
                    >
                      <div className="flex items-center gap-[5px]">
                        <ProfileIcon dimension={33} className="!p-2" />
                        <div className="flex items-center gap-[2px]">
                          <p className="text-sm font-bold text-grey-900">
                            User ∙
                          </p>
                          <p className="text-sm text-grey-600">
                            {formatDateAndTime(user?.createdAt)}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {user.tickets?.map((t, ticketIdx) => {
                          const colorPair = getColorForTicket(
                            t.ticketId.name,
                            usedColors,
                          );
                          usedColors.push(colorPair);
                          const [textColor, bgColor] = colorPair.split(",");

                          return (
                            <Pill
                              key={`${t.ticketId._id}-${ticketIdx}`}
                              text={`${t.quantity}X ${t.ticketId.name}`}
                              variation="green"
                              className="!line-clamp-1 !w-full !min-w-0 !max-w-fit !overflow-hidden !text-left"
                              style={{
                                color: textColor,
                                backgroundColor: bgColor,
                              }}
                            />
                          );
                        })}
                      </div>
                    </div>,
                  ],
                };
              }) ?? []
            }
            loadingRows={query.limit}
            isLoading={fetchingEventDetails}
            isRowClickable={false}
            isTableEmpty={[0, undefined].includes(
              guestData?.data?.data?.length,
            )}
            emptyTitle="No Sales Yet"
            emptySubTitle={`Sales breakdown would appear when ${eventData?.data?.createdBy?.firstName} publishes this event`}
            emptyImage="/assets/images/paper-plane-glass.svg"
            isPaginated
            currentPage={query.page ?? 1}
            totalCount={guestData?.data?.totalCount ?? 0}
            itemsPerPage={query.limit!}
            pageRangeDisplayed={3}
            onPageChange={(page) => {
              setQuery({
                ...query,
                page: page,
              });
            }}
            className=""
          />
        </BgWrapper>
      </div>
    </div>
  );
};
